/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Data Extraction", description = "Write back the data from your ad platforms to your warehouse", lastUpdatedDate = new Date(1718841600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Data Extraction",
        description: "Write back the data from your ad platforms to your warehouse",
        lastUpdatedDate: "Thu Jun 20 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Audience size report",
          "depth": 0,
          "id": "audience-size-report"
        }, {
          "value": "How it works",
          "depth": 1,
          "id": "how-it-works"
        }, {
          "value": "Data structure",
          "depth": 1,
          "id": "data-structure"
        }, {
          "value": "Supported destinations",
          "depth": 0,
          "id": "supported-destinations"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      img: "img",
      ol: "ol",
      li: "li",
      a: "a",
      code: "code",
      h3: "h3",
      ul: "ul",
      em: "em"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch's data extraction feature allows you to write back the data from your\nad platforms to your data system. With this data, you can conduct dynamic queries\nand generate insightful reports for stakeholders."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/data-extraction-outline.png",
          alt: "Data extraction outline"
        })
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsx(_components.p, {
        children: "To get started, you will need:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["A source with ", _jsx(_components.a, {
            href: "/syncs/lightning-sync-engine",
            children: "lightning engine"
          }), " so Hightouch has write-back privileges"]
        }), "\n", _jsxs(_components.li, {
          children: ["A destination to any of the ", _jsx(_components.a, {
            href: "#supported-destinations",
            children: "supported"
          }), " ad platforms"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If your destination supports this feature, you'll see a ", _jsx(_components.code, {
          children: "Data Extraction"
        }), " tab on the destination's page.\nIn this tab, you can enable this feature and select which data system to write to (one of your configured sources).\nCurrently, Hightouch only supports the ", _jsx(_components.code, {
          children: "Audience size report"
        }), " so by turning this feature on,\nHightouch will write this report back to your data system."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/data-extraction-tab.png",
          alt: "Data extraction tab"
        })
      }), "\n", _jsx(_components.h2, {
        id: "audience-size-report",
        children: "Audience size report"
      }), "\n", _jsx(_components.p, {
        children: "The audience size report extracts a daily historical log of the estimated size of your audiences in each ad platform, including metadata about the connected Hightouch destination and sync (when applicable)."
      }), "\n", _jsx(_components.h3, {
        id: "how-it-works",
        children: "How it works"
      }), "\n", _jsx(_components.p, {
        children: "For each enabled destination:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Hightouch schedules a daily job to fetch and write the data from the ad platform to your data system"
        }), "\n", _jsxs(_components.li, {
          children: ["This job pulls ", _jsx(_components.em, {
            children: "all"
          }), " audiences from the active ad account(s) authorized by the user"]
        }), "\n", _jsx(_components.li, {
          children: "Each job is a snapshot of the audiences at that point in time, so no previous data is updated or deleted"
        }), "\n", _jsxs(_components.li, {
          children: ["The data is then written back to your data system under the ", _jsx(_components.code, {
            children: "hightouch_audit"
          }), " schema."]
        }), "\n", _jsxs(_components.li, {
          children: ["This data lives in its own designated table: ", _jsx(_components.code, {
            children: "{destination-type}_{destination-id}_audiences"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch also creates a view that combines all the different destination tables into one, allowing you\nto query the data holistically."
      }), "\n", _jsx(_components.h3, {
        id: "data-structure",
        children: "Data structure"
      }), "\n", _jsxs(_components.p, {
        children: ["You can find all of the data from this feature in the ", _jsx(_components.code, {
          children: "hightouch_audit"
        }), " schema."]
      }), "\n", _jsxs(_components.p, {
        children: ["There will be one table per enabled Hightouch destination (", _jsx(_components.code, {
          children: "{destination-type}_{destination-id}_audiences"
        }), ").\nEach destination table's schema closely mirrors the ad platform's API's schema. Hightouch tries to\npreserve the data in a form as close to the original API response as possible so you can adapt and\ntransform the data according to your specific needs."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch combines the table into a view, ", _jsx(_components.code, {
          children: "external_audiences_metadata_v1"
        }), ", with a standardized schema of:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "ad_account_id"
        }), "\n", _jsx(_components.li, {
          children: "audience_id"
        }), "\n", _jsx(_components.li, {
          children: "audience_name"
        }), "\n", _jsx(_components.li, {
          children: "audience_size"
        }), "\n", _jsx(_components.li, {
          children: "ht_fetched_at"
        }), "\n", _jsx(_components.li, {
          children: "ht_destination"
        }), "\n", _jsx(_components.li, {
          children: "ht_destination_id"
        }), "\n", _jsxs(_components.li, {
          children: ["ht_sync_id (will be ", _jsx(_components.code, {
            children: "null"
          }), " if there are no HT syncs for this audience)"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "To ensure consistency and backwards compatibility, our combined view is versioned.\nAny updates or changes to the view schema will result in a new version (e.g., v2, v3, etc.).\nPrevious versions of the view will remain accessible as long as it existed before a new updated version."
      }), "\n", _jsx(_components.h2, {
        id: "supported-destinations",
        children: "Supported destinations"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/amazon-ads",
            children: "Amazon Ads DSP"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/facebook",
            children: "Facebook Custom Audiences"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/google",
            children: "Google Ads"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/bingads",
            children: "Microsoft Bing Ads Audiences"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/snapchat",
            children: "Snapchat"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/destinations/tiktok",
            children: "TikTok"
          })
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
